import { AppConfig } from '@ve/types';
import Vue from 'vue';
import AdModal from '@ve/components/adModal/AdModal.vue';
import '@fr/src/css/common/promo-tooltip.scss';
import {DataLayerService, LINK_BEHAVIOUR_MAP, BUTTON_BEHAVIOUR_MAP, PromotionEvent} from './DataLayerService';
// @ts-expect-error Не настроены alias
import getPageTypeService from "@fr/src/js/common/Domain/Service/getPageType";

type TooltipData = {
    imageLink: string,
    title: string,
};

type ExtendedPopupInstance = Vue & { open: () => void };

export const getPromoDataFromElement = (
    element: HTMLElement,
    creative_slot?: string
): PromotionEvent => ({
    id: element.dataset.promoTitle || '',
    creative: element.dataset.promoCreative || '',
    ...(creative_slot?.trim() && { creative_slot: creative_slot.trim() }),
    position: getPageTypeService()
});

const sendPromoClick = (
    element: HTMLElement,
    behaviour: keyof typeof LINK_BEHAVIOUR_MAP | keyof typeof BUTTON_BEHAVIOUR_MAP,
    type: 'link' | 'button' = 'link'
) => {
    const map = type === 'link' ? LINK_BEHAVIOUR_MAP : BUTTON_BEHAVIOUR_MAP;
    const creative_slot = map[behaviour as keyof typeof map];

    if (!creative_slot) {
        return;
    }

    DataLayerService.sendPromoClick({
        ...getPromoDataFromElement(element, creative_slot),
        creative_slot
    });
};

const sendPromoView = (element: HTMLElement) => {
    DataLayerService.sendPromoView({
        ...getPromoDataFromElement(element),
    });
};

interface ExWindow extends Window {
    appConfig: AppConfig;
    copyPromo: (target: HTMLElement) => void;
    sendPromoClick: typeof sendPromoClick;
    sendPromoView: typeof sendPromoView;
}
declare let window: ExWindow;

window.sendPromoClick = sendPromoClick;
window.sendPromoView = sendPromoView;

const getPromo = (): Record<string, any> | null => {
    const promo = window.appConfig?.promo;
    if (!promo) return null;

    Object.keys(promo.parts || {}).forEach((partKey) => {
        const part = promo.parts[partKey];
        if (part?.start && part?.end) {
            const startDate = new Date(part.start);
            const endDate = new Date(part.end);
            const currentDate = new Date();
            part.active = currentDate >= startDate && currentDate <= endDate;
        }
    });

    return promo;
};

const showCopiedTooltip = (target: HTMLElement): void => {
    const tooltip = document.createElement('div');
    tooltip.className = 'copied-tooltip';
    tooltip.textContent = 'Промокод скопирован';

    document.body.appendChild(tooltip);

    const targetRect = target.getBoundingClientRect();
    tooltip.style.top = `${targetRect.top - tooltip.offsetHeight - 10}px`;
    tooltip.style.left = `${targetRect.left + (targetRect.width - tooltip.offsetWidth) / 2}px`;

    setTimeout(() => {
        tooltip.remove();
    }, 5_000);
};

const copyPromo = (target: HTMLElement): void => {
    const value = target.getAttribute('data-coupon') || '';
    const buttonText = target.querySelector<HTMLElement>('.button-text');
    const buttonTextCopied = target.querySelector<HTMLElement>('.button-text-copied');

    if (buttonText && buttonTextCopied) {
        target.classList.add('clicked');
        buttonText.style.display = 'none';
        buttonTextCopied.style.display = 'flex';
        buttonTextCopied.style.alignItems = 'center';

        setTimeout(() => {
            target.classList.remove('clicked');
            buttonText.style.display = 'inline-flex';
            buttonTextCopied.style.display = 'none';
        }, 5_000);
    }

    if (target.classList.contains('js-copy-promo')) {
        const tooltipWrapper = target.closest('.tooltip-wrapper');
        const tooltipElements: HTMLElement[] = [
            tooltipWrapper?.querySelector('.tooltip-container') as HTMLElement | null,
            tooltipWrapper?.querySelector('.tooltip-popover__content') as HTMLElement | null,
        ].filter((element): element is HTMLElement => Boolean(element));

        if (tooltipElements.length) {
            tooltipElements.forEach((element) => {
                element.style.display = 'flex';
            });

            setTimeout(() => {
                tooltipElements.forEach((element) => {
                    element.style.display = 'none';
                });
            }, 5_000);
        }
    }

    navigator.clipboard?.writeText(value)
        .then(() => {
            showCopiedTooltip(target);
        });
};

window.copyPromo = copyPromo;

const isWithinDateRange = (start?: string, end?: string): boolean => {
    const currentDate = new Date();

    if (start) {
        const startDate = new Date(start);
        if (currentDate < startDate) return false;
    }

    if (end) {
        const endDate = new Date(end);
        if (currentDate > endDate) return false;
    }

    return true;
};

const isModalAvailable = (): boolean => {
    const popupData = window.appConfig?.promo?.parts?.popup;
    const isPopupEnabled = popupData?.active;
    if (!isPopupEnabled) {
        return false;
    }

    if (!isWithinDateRange(popupData.start, popupData.end)) {
        return false;
    }

    const savedDate = localStorage.getItem('adModalLastViewDate');
    if (!savedDate) {
        return true;
    }

    const savedDateObj = new Date(savedDate);
    const currentDate = new Date();
    const differenceInDays = (currentDate.getTime() - savedDateObj.getTime()) / (1000 * 60 * 60 * 24);

    return differenceInDays >= (popupData?.showDaysInterval || 3);
};

const initAdModal = (): void => {
    setTimeout(() => {
        const promo = getPromo();
        if (promo && !document.querySelector('.js-ad-modal') && isModalAvailable()) {
            const popupData = promo.parts?.popup;
            const modal = document.createElement('div');
            modal.className = 'js-ad-modal';
            document.getElementsByTagName('body')[0].appendChild(modal);
            new Vue({
                render: (h) => h(AdModal, {
                    props: {
                        ...popupData,
                        preventLocalStorage: false,
                    },
                }),
            }).$mount('.js-ad-modal');
        }
    }, 3000);
};

const initTooltipPromoPopup = (() => {
    let popup: Vue | null = null;

    return () => {
        const popupData = window.appConfig?.promo?.parts?.offer?.popup;
        if (!popup) {
            const coupon = null;
            const buttonText = 'Понятно';
            const modal = document.createElement('div');
            modal.className = 'js-tooltip-modal';
            document.body.appendChild(modal);

            const adaptedPopupData = {
                ...popupData,
                link: {
                    behaviour: 'openNewPage' as const,
                    text: popupData?.linkText || '',
                    value: popupData?.linkUrl || '',
                },
                button: {
                    behaviour: 'close' as const,
                    text: buttonText,
                    value: '',
                },
            };

            [popup] = new Vue({
                render: (h) => h(AdModal, {
                    props: {
                        ...adaptedPopupData,
                        coupon,
                        preventLocalStorage: true,
                    },
                }),
            }).$mount('.js-tooltip-modal').$children;

            return;
        }

        (popup as ExtendedPopupInstance).open();
    };
});

const openTooltipPopup = initTooltipPromoPopup();

const initPromoTooltip = (el: HTMLElement, tooltipData: TooltipData): void => {
    if (el.children.length) return;

    const tooltip = document.createElement('div');
    tooltip.className = 'promo-tooltip';

    const imageStyle = tooltipData.imageLink ? `style="background-image:url('${tooltipData.imageLink}');"` : '';
    const textContent = tooltipData.title ? tooltipData.title : '';

    tooltip.innerHTML = `
        <div class="promo-tooltip__image" ${imageStyle}></div>
        <div class="promo-tooltip__text">${textContent}</div>
        <div class="promo-tooltip__icon"></div>
    `;

    tooltip.addEventListener('click', (e) => {
        e.preventDefault();
        openTooltipPopup();
    });

    el.appendChild(tooltip);
};

const initCommonPromoTooltip = (el: HTMLElement | null): void => {
    const { offer } = window.appConfig?.promo?.parts || {};
    if (!el || !offer) return;

    if (!isWithinDateRange(offer.start, offer.end)) return;

    const tooltipData = {
        imageLink: offer.imageLink,
        title: offer.title,
    };

    initPromoTooltip(el, tooltipData);
};

const initSegmentPromoTooltip = (el: HTMLElement | null, segmentId?: number | null): void => {
    const { offer } = window.appConfig?.promo?.parts || {};
    if (!el || !segmentId || !offer) return;

    if (!isWithinDateRange(offer.start, offer.end)) return;

    const segment = offer.segments?.[segmentId];
    if (!segment) return;

    const tooltipData = {
        imageLink: segment.imageLink,
        title: segment.title,
    };

    initPromoTooltip(el, tooltipData);
};

export {
    getPromo,
    copyPromo,
    isModalAvailable,
    initAdModal,
    initCommonPromoTooltip,
    initSegmentPromoTooltip,
    sendPromoClick,
    sendPromoView
};
